import {Alert, Modal, Table, Typography} from 'antd';
import {
  AccountInvoiceFieldsFragment,
  InvoiceLineItemUnitType,
  useCreateAccountInvoiceMutation
} from 'api/generated';
import {PropertyList} from 'components';
import {Formik} from 'formik';
import {DatePicker, Form, FormItem, Radio, Select} from 'formik-antd';
import {FormikHelpers} from 'formik/dist/types';
import {formatIBAN, formatIntAmount} from 'lib/formaters';
import moment, {Moment} from 'moment-timezone';
import React, {useState} from 'react';
import styled from 'styled-components';

type Props = {
  accountId: string;
  iban?: string | null;
};

enum filterType {
  custom,
  lastWeek,
  lastMonth
}

type FormValues = {
  filter: filterType;
  timezone: string;
  range: Moment[];
};

const RadioBlock = styled(Radio)`
  display: block !important;
  line-height: 34px !important;
  height: 34px !important;
`;

const Label = styled.span`
  display: inline-block;
  width: 150px;
`;

const RangePicker = styled(DatePicker.RangePicker)`
  width: 280px;
`;

const {Option} = Select;
const {Text} = Typography;
const timezones = moment.tz.names();

const percent = new Intl.NumberFormat(undefined, {
  style: 'percent',
  maximumFractionDigits: 2
});

const getCustomRangeLabel = (range: Moment[], timezone: string) => {
  const [start, end] = range;
  if (start.isSame(end, 'day')) return start.tz(timezone).format('ll z');
  return `${start.tz(timezone).format('ll z')} - ${end.tz(timezone).format('ll z')}`;
};

export const PreviewInvoice: React.FC<Props> = ({accountId, iban}) => {
  const [createInvoice] = useCreateAccountInvoiceMutation();
  const [invoice, setInvoice] = useState<AccountInvoiceFieldsFragment | null>(null);
  const [timezone, setTimezone] = useState('UTC');
  const [isOpen, setOpen] = useState(false);
  const [isInvoiceOpen, setInvoiceOpen] = useState(false);

  const initialValues: FormValues = {
    filter: filterType.lastWeek,
    timezone,
    range: [
      moment().subtract(1, 'weeks').startOf('isoWeek'),
      moment().subtract(1, 'weeks').endOf('isoWeek')
    ]
  };

  const handleSubmit = async (
    values: FormValues,
    {setSubmitting, resetForm}: FormikHelpers<FormValues>
  ) => {
    const [start, end] = values.range;
    const timezone = values.timezone;
    let from = start.startOf('day').tz(timezone).add(12, 'hours').startOf('day').unix();
    let to = end.endOf('day').tz(timezone).subtract(12, 'hours').endOf('day').unix();
    if (values.filter === filterType.lastWeek) {
      from = moment().tz(timezone).subtract(1, 'weeks').startOf('isoWeek').unix();
      to = moment().tz(timezone).subtract(1, 'weeks').endOf('isoWeek').unix();
    }
    if (values.filter === filterType.lastMonth) {
      from = moment().tz(timezone).subtract(1, 'month').startOf('month').unix();
      to = moment().tz(timezone).subtract(1, 'month').endOf('month').unix();
    }
    const {data} = await createInvoice({
      variables: {
        input: {
          accountId,
          timezone,
          from,
          to
        }
      }
    });
    if (!data) return;
    setTimezone(timezone);
    setInvoice(data.createAccountInvoice);
    setTimeout(() => {
      setInvoiceOpen(true);
    }, 250);
    setSubmitting(false);
    setOpen(false);
    setTimeout(resetForm, 100);
  };

  const handleInvoiceClose = () => {
    setInvoiceOpen(false);
    setTimeout(() => {
      setInvoice(null);
    }, 250);
  };

  return (
    <>
      <span onClick={() => setOpen(true)}>Preview invoice</span>
      <Formik initialValues={initialValues} onSubmit={handleSubmit} enableReinitialize={!isOpen}>
        {({submitForm, isSubmitting, resetForm, values, setFieldValue}) => (
          <Modal
            width={650}
            title="Preview invoice"
            okText="Preview"
            cancelText="Cancel"
            open={isOpen}
            confirmLoading={isSubmitting}
            onOk={submitForm}
            onCancel={() => {
              setOpen(false);
              setTimeout(resetForm, 100);
            }}>
            {values.timezone !== 'UTC' && (
              <Alert
                style={{marginBottom: 20}}
                type="warning"
                message="Careful. Automatic invoices are created in UTC timezone, generating an invoice in
                  another timezone can lead to inconsistency."
              />
            )}
            <Form layout="vertical">
              <FormItem
                name="timezone"
                label="Timezone"
                extra={<>All the dates will be formatted in the selected timezone.</>}>
                <Select
                  name="timezone"
                  showSearch
                  onChange={(tz) => {
                    setFieldValue('range', [
                      moment().tz(tz).subtract(1, 'weeks').startOf('isoWeek'),
                      moment().tz(tz).subtract(1, 'weeks').endOf('isoWeek')
                    ]);
                  }}>
                  {timezones.map((tz) => (
                    <Option key={tz} value={tz}>
                      {tz}
                    </Option>
                  ))}
                </Select>
              </FormItem>
              <FormItem name="filter" label="Filters">
                <Radio.Group name="filter">
                  <RadioBlock value={filterType.lastWeek} name="filter">
                    <Label>Last week</Label>
                    <Text type="secondary">
                      {moment()
                        .tz(values.timezone)
                        .subtract(1, 'weeks')
                        .startOf('isoWeek')
                        .format('ll z')}{' '}
                      -{' '}
                      {moment()
                        .tz(values.timezone)
                        .subtract(1, 'weeks')
                        .endOf('isoWeek')
                        .format('ll z')}
                    </Text>
                  </RadioBlock>
                  <RadioBlock value={filterType.lastMonth} name="filter">
                    <Label>Last month</Label>
                    <Text type="secondary">
                      {moment()
                        .tz(values.timezone)
                        .subtract(1, 'month')
                        .startOf('month')
                        .format('ll z')}{' '}
                      -{' '}
                      {moment()
                        .tz(values.timezone)
                        .subtract(1, 'month')
                        .endOf('month')
                        .format('ll z')}
                    </Text>
                  </RadioBlock>
                  <RadioBlock value={filterType.custom} name="filter">
                    <Label>Custom</Label>
                    {values.filter === filterType.custom ? (
                      <RangePicker
                        name="range"
                        size="small"
                        format="ll"
                        allowClear={false}
                        disabledDate={(date) =>
                          date ? date.isAfter(moment().endOf('day')) : false
                        }
                      />
                    ) : (
                      <Text type="secondary">
                        {getCustomRangeLabel(values.range, values.timezone)}
                      </Text>
                    )}
                  </RadioBlock>
                </Radio.Group>
              </FormItem>
              <input type="submit" value="submit" hidden />
            </Form>
          </Modal>
        )}
      </Formik>
      <Modal
        bodyStyle={{padding: 0}}
        width={650}
        title="Invoice"
        cancelText="Cancel"
        footer={false}
        maskClosable={false}
        open={isInvoiceOpen}
        onCancel={handleInvoiceClose}>
        {invoice && (
          <Table
            pagination={false}
            dataSource={invoice.lineItems}
            bordered
            title={() => (
              <PropertyList
                properties={[
                  {
                    label: 'Period',
                    value: getCustomRangeLabel(
                      [moment.unix(invoice!.period.from), moment.unix(invoice!.period.to)],
                      timezone
                    )
                  },
                  {
                    label: 'IBAN',
                    value: formatIBAN(iban, true),
                    copyable: {text: iban!}
                  }
                ]}
              />
            )}
            columns={[
              {
                title: 'Name',
                dataIndex: 'name',
                key: 'name'
              },
              {
                title: 'Quantity',
                dataIndex: 'quantity',
                key: 'quantity',
                align: 'right',
                render: (value = 0, line) => {
                  if (line.unitType === InvoiceLineItemUnitType.PERCENT) {
                    return formatIntAmount(value);
                  }
                  return value.toLocaleString();
                }
              },
              {
                title: 'Fee',
                dataIndex: 'unitAmount',
                key: 'unitAmount',
                align: 'right',
                render: (value = 0, line) => {
                  if (line.unitType === InvoiceLineItemUnitType.MONEY) {
                    return formatIntAmount(value);
                  }
                  return percent.format(value / 100);
                }
              },
              {
                title: 'Amount',
                dataIndex: 'amount',
                key: 'amount',
                align: 'right',
                render: (value = 0) => formatIntAmount(value)
              }
            ]}
            summary={() => (
              <>
                <Table.Summary.Row style={{fontWeight: 'bold', textAlign: 'right'}}>
                  <Table.Summary.Cell index={0} colSpan={2} rowSpan={3} />
                  <Table.Summary.Cell index={1}>Subtotal</Table.Summary.Cell>
                  <Table.Summary.Cell index={2}>
                    {formatIntAmount(invoice!.amount)}
                  </Table.Summary.Cell>
                </Table.Summary.Row>
                <Table.Summary.Row style={{fontWeight: 'bold', textAlign: 'right'}}>
                  <Table.Summary.Cell index={1}>VAT</Table.Summary.Cell>
                  <Table.Summary.Cell index={2}>{formatIntAmount(invoice!.tax)}</Table.Summary.Cell>
                </Table.Summary.Row>
                <Table.Summary.Row style={{fontWeight: 'bold', textAlign: 'right'}}>
                  <Table.Summary.Cell index={1}>Total</Table.Summary.Cell>
                  <Table.Summary.Cell index={2}>
                    {formatIntAmount(invoice!.totalAmount)}
                  </Table.Summary.Cell>
                </Table.Summary.Row>
              </>
            )}
          />
        )}
      </Modal>
    </>
  );
};
